import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { get, isEmpty, isArray } from 'lodash';
import { INavigationItem } from '@nrwl/navigation-abstract';
import {
  BreadCrumbService,
  CommonLibModule,
  FILTER_KEYS,
  ITestList,
  TestListEntity,
  TestListService,
  TimeoutWrapperService,
  NotificationService
} from '@nrwl/common-lib';
import * as config from '../../entities/config';
import { TestListTableComponent } from '../test-list-table/test-list-table.component';
import { CommonMaterialModule } from '@nrwl/common-material';

const dataConfig = require('../../json/monitorConfig.json');

@Component({
  selector: 'test-list-page',
  standalone: true,
  imports: [CommonModule, TestListTableComponent, CommonLibModule, CommonMaterialModule],
  templateUrl: './test-list-page.component.html',
  styleUrls: ['./test-list-page.component.scss']
})
export class TestListPageComponent implements OnInit, OnDestroy {
  isDataAvailable: boolean;
  dataSource: BehaviorSubject<ITestList[]>;
  refreshTable: BehaviorSubject<boolean>;
  listDataSource: Array<TestListEntity>;
  dataConfig: any; //eslint-disable-line
  cacheObj: Subscription;
  loadingObj: Subscription;
  title: string;
  layeredSource = [];
  filterQuery: Array<{ keyword: string; value: string }> = [];
  columnHeader: Array<string>;
  tableId = 'test-list';
  isLoading = true;
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly testListService: TestListService,
    private readonly timeout: TimeoutWrapperService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly breadCrumbService: BreadCrumbService,
    private readonly notificationService: NotificationService
  ) {
    this.dataSource = new BehaviorSubject<ITestList[]>([]);
    this.refreshTable = new BehaviorSubject<boolean>(false);
  }

  ngOnInit(): void {
    this.dataConfig = dataConfig;
    this.handleQueryParams();
    this.isDataAvailable = false;
    this.testListService.updateTestResponse();
    if (this.dataConfig) {
      this.title = get(this.dataConfig, 'title', '');
      this.columnHeader = this.dataConfig.culumnHeader;
    }
    this.cacheObj = this.testListService.cache.subscribe(data => {
      this.listDataSource = this.testListService.sortDataByName(data);
      this.applySearchFilter();
    });
    this.loadingObj = this.testListService.dataLoading.subscribe(loading => {
      this.isLoading = loading;
    });
    this._setBreadCrumbs();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    let moduleName = '';
    let moduleEnabled = 'true';
    if (event.ctrlKey && event.altKey) {
      if (event.which === 90) {
        //Alt+z
        moduleName = 'ZTP Discovery';
        moduleEnabled = sessionStorage.getItem('ZTPDiscovery');
        moduleEnabled = moduleEnabled === 'true' ? 'false' : 'true';
        sessionStorage.setItem('ZTPDiscovery', moduleEnabled);
      }
      if (moduleName) {
        const message =
          moduleEnabled === 'true'
            ? moduleName + ' is enabled and its only available for demonstration purposes'
            : moduleName + ' is disabled';
        this.notificationService.onSuccess(message);
      }
    }
  }

  ngOnDestroy() {
    if (this.cacheObj) {
      this.cacheObj.unsubscribe();
    }
    this.loadingObj?.unsubscribe();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  handleQueryParams(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe(params => {
      this.filterQuery = [];
      let filteredParams = params['test_list_filter'];
      filteredParams = filteredParams ? JSON.parse(filteredParams) : filteredParams;

      if (filteredParams) {
        const filteredParamArray = filteredParams.split('&');
        filteredParamArray.forEach(param => {
          this.filterQuery.push({
            keyword: param.split('=')[0].trim(),
            value: param.split('=')[1].trim()
          });
        });
      }
      this.applySearchFilter();
      this.layeredSource = isArray(params['layeredSource'])
        ? params['layeredSource']
        : params['layeredSource']
        ? [params['layeredSource']]
        : params['layeredSource'];
      this._setBreadCrumbs();
    });
  }

  applySearchFilter(): void {
    const filteredData = this.applyFreeFormFilters();
    this.dataSourceChanged(filteredData);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  dataSourceChanged(dataSource: any): void {
    this.dataSource.next(dataSource);
  }

  applyFreeFormFilters(): TestListEntity[] {
    const dataSource = this.listDataSource;
    if (isEmpty(this.filterQuery)) {
      return dataSource;
    }
    return dataSource?.filter(data => {
      return this.filterQuery.every(filter => {
        const filteredValue = filter.value?.toLowerCase();
        const filterConfig = Object.assign({}, config.FilterConfig[filter.keyword]);
        if (filterConfig) {
          return filterConfig.fieldNames.some((fieldName: any) => {
            return filterConfig.source === 'includes'
              ? data[fieldName]()?.toLowerCase().includes(filteredValue)
              : data[fieldName]()?.toLowerCase() === filteredValue;
          });
        }
      });
    });
  }

  hasData(status: boolean): void {
    this.timeout.set(
      'checkDataAvailable',
      () => {
        this.isDataAvailable = status;
      },
      0
    );
  }

  refreshTestList(): void {
    this.refreshTable.next(true);
  }

  private _setBreadCrumbs(): void {
    const breadCrumbValues: Array<INavigationItem> = [];
    if (this.layeredSource?.length) {
      this.layeredSource.forEach(breadCrumbLabel => {
        breadCrumbValues.push({ label: breadCrumbLabel });
      });
    }
    breadCrumbValues.push({
      label: 'Test list'
    });
    this.breadCrumbService.setBreadCrumbs(breadCrumbValues);
  }
}
